import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AppState, useAppDispatch } from '@/store';
import ProductsSlider from '../ProductsSlider';
import { CartItemAddActionTypes } from '../../../constants/cart.constants';
import CustomModal, { CustomModalRef } from '../CustomModal';
import ErrorBoundary from '../../tailwind/ErrorBoundary';
import Button from '../Button';
import AddedToCartModalItem from './AddedToCartModalItem';
import FreeGiftMeter from '../FreeGiftMeter';
import { useCartItems } from '@features/cart/hooks/useCartItems';
import LoadingSpinner from '../LoadingSpinner';
import FreePromoMeter from '../FreePromoMeter';
import cx from '@utils/cx.util';
import { useTieredDiscount } from '@features/pdp/hooks/useTieredDiscount';
import { TieredDiscountProgress } from '@features/pdp/components/TieredDiscount/TieredDiscountProgress';

const AddedToCartModal = ({
  className = '',
  title = 'Added To Cart',
  id = 'AddedToCartModal',
  relatedItems = [],
}) => {
  const dispatch = useAppDispatch();
  const { items } = useSelector((state: any) => state.cartItemAdd);
  const { potentialFreeGift } = useSelector((state: AppState) => state.cartItemList);
  const customModalRef = useRef<CustomModalRef>(null);
  const { isLoading, data } = useCartItems(items.length > 0);
  const {
    isVisible: isTieredDiscountVisible,
    currentProgress,
    nextTierInfo,
    tiers,
    currentSubtotal,
  } = useTieredDiscount(data);

  useEffect(
    () => () => {
      dispatch({ type: CartItemAddActionTypes.RESET });
    },
    [dispatch]
  );

  return (
    <CustomModal ref={customModalRef} id={id} title={title} size='sm' className={className} defaultShown>
      {isLoading ? (
        <LoadingSpinner small withContainer />
      ) : (
        <>
          {isTieredDiscountVisible && (
            <TieredDiscountProgress
              currentProgress={currentProgress}
              tiers={tiers}
              nextTierInfo={nextTierInfo}
              currentSubtotal={currentSubtotal}
              className='tw-mb-4 tw-px-4'
              variant='transparent'
            />
          )}

          {items.map((i) => (
            <AddedToCartModalItem key={i.id} item={i} />
          ))}

          {!isTieredDiscountVisible && (
            <>
              <FreePromoMeter
                className={cx('tw-border-t', { 'tw-border-b-0': potentialFreeGift?.length > 0 })}
              />

              {potentialFreeGift?.length > 0 && (
                <div className='tw-border-t tw-border-neutrals-light-grey'>
                  {potentialFreeGift
                    .filter((g) => g.meterProgress >= 0.5)
                    .map((gift, i) => (
                      <FreeGiftMeter key={i} {...gift} />
                    ))}
                </div>
              )}
            </>
          )}

          <div className='tw-mx-auto tw-mb-auto tw-mt-8 tw-max-w-xs'>
            <Button variant='primary' fullWidth='all' onClick={() => customModalRef.current.closeModal()}>
              Continue Shopping
            </Button>
            <Button
              variant='unstyled'
              className='tw-mt-2 tw-uppercase tw-text-body'
              href={jsRoutes.controllers.Cart.view().url}
              fullWidth='all'>
              View Cart &amp; Checkout
            </Button>
          </div>

          {relatedItems?.length > 0 && (
            <ErrorBoundary
              inlineError
              message={
                <>
                  Oops! Something went wrong while loading similar items.
                  <br />
                  <br />
                  We're working hard to fix it. Please try again shortly.
                </>
              }>
              <ProductsSlider
                starsColor='var(--brand-black)'
                showAddToCart
                products={relatedItems}
                title='You may also like:'
                slidesToShow={1}
                starsSize='16px'
                className='tw-border-t-solid tw-mb-4 tw-mt-4 tw-border-t tw-border-t-gray-300 tw-pt-4'
              />
            </ErrorBoundary>
          )}
        </>
      )}
    </CustomModal>
  );
};

export default AddedToCartModal;
